import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import WindowsMt5DownloadModal from '../../../components/download/WindowsMt5DownloadModal';
import { useWindowsMt5Download } from '../../../hooks/useWindowsMt5Download';
import { getAndroidDownloadRouteUrl } from '../../../utils/androidDownload';
import { getWindowsMt5DownloadRouteUrl } from '../../../utils/windowsDownload';

export default function MT5Section() {
  const { t } = useTranslation();

  const windowsDownloadLink = getWindowsMt5DownloadRouteUrl();
  const appStoreLink = 'https://apps.apple.com/us/app/metatrader-5/id413251709';
  const androidDownloadLink = getAndroidDownloadRouteUrl();
  const {
    showWindowsMt5Modal,
    checkingWindowsMt5Region,
    closeWindowsMt5Modal,
    handleWindowsMt5DownloadClick,
  } = useWindowsMt5Download();

  const features = [
    {
      icon: 'ri-line-chart-line',
      title: t('home.ac'),
      description: t('home.pta'),
    },
    {
      icon: 'ri-smartphone-line',
      title: t('home.mpa'),
      description: t('home.tsa'),
    },
    {
      icon: 'ri-shield-check-line',
      title: t('home.st'),
      description: t('home.bls'),
    },
    {
      icon: 'ri-time-line',
      title: t('home.247'),
      description: t('home.rtc'),
    },
    {
      icon: 'ri-robot-line',
      title: t('home.at'),
      description: t('home.ea'),
    },
    {
      icon: 'ri-speed-up-line',
      title: t('home.lfe'),
      description: t('home.ull'),
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none'%3E%3Ccircle cx='30' cy='30' r='2' fill='%23ffffff' fill-opacity='0.1'/%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <i className="ri-computer-line"></i>
            {t('home.tradingPlat')}
          </div>

          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            {t('home.tradingWith')}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-400">
              {t("home.mt5Platform", { defaultValue: "MT5 Platform" })}
            </span>
          </h2>

          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {t('home.exp')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Features */}
          <div className="space-y-8">
            <div className="grid gap-6">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-lg flex items-center justify-center">
                    <i className={`${feature.icon} text-white text-xl`} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{feature.title}</h3>
                    <p className="text-gray-300">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* DOWNLOAD CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-8">

              {/* Windows */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col items-center gap-4 hover:bg-white/10 transition">
                <button
                  type="button"
                  onClick={handleWindowsMt5DownloadClick}
                  disabled={checkingWindowsMt5Region}
                  className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-6 py-3 rounded-xl font-semibold flex justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <i className="ri-windows-fill"></i>
                  {checkingWindowsMt5Region ? t("download.checkingRegion", { defaultValue: "識別中..." }) : t('home.dlMT5')}
                </button>

                <a href={windowsDownloadLink} target="_blank" rel="noopener noreferrer">
                  <div className="bg-white p-3 rounded-xl cursor-pointer hover:scale-105 transition">
                    <QRCode value={windowsDownloadLink} size={120} />
                  </div>
                </a>

                <div className="text-xs text-gray-400 text-center">
                  {t("download.scanWindows", { defaultValue: "Scan or click to download (Windows)" })}
                </div>
              </div>

              {/* iOS */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col items-center gap-4 hover:bg-white/10 transition">
                <a href={appStoreLink} className="w-full">
                  <button className="w-full bg-black text-white px-6 py-3 rounded-xl flex justify-center gap-2">
                    <i className="ri-apple-fill text-xl"></i>
                    IOS
                  </button>
                </a>

                <a href={appStoreLink} target="_blank" rel="noopener noreferrer">
                  <div className="bg-white p-3 rounded-xl cursor-pointer hover:scale-105 transition">
                    <QRCode value={appStoreLink} size={120} />
                  </div>
                </a>

                <div className="text-xs text-gray-400 text-center">
                  {t("download.scanIos", { defaultValue: "Scan or click for iOS" })}
                </div>
              </div>

              {/* Android */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col items-center gap-4 hover:bg-white/10 transition">
                <a href={androidDownloadLink} className="w-full">
                  <button className="w-full bg-black text-white px-6 py-3 rounded-xl flex justify-center gap-2">
                    <i className="ri-android-fill text-xl"></i>
                    Android
                  </button>
                </a>

                <a href={androidDownloadLink} target="_blank" rel="noopener noreferrer">
                  <div className="bg-white p-3 rounded-xl cursor-pointer hover:scale-105 transition">
                    <QRCode value={androidDownloadLink} size={120} />
                  </div>
                </a>

                <div className="text-xs text-gray-400 text-center">
                  {t("download.scanAndroid", { defaultValue: "Scan or click for Android" })}
                </div>
              </div>

            </div>
          </div>

          {/* Preview */}
          <div>
            <img
              src="/img/5d6ae696c70233db63b15b078f5f2970.webp"
              className="w-full h-80 object-cover rounded-lg"
            />
          </div>
        </div>
      </div>

      <WindowsMt5DownloadModal open={showWindowsMt5Modal} onClose={closeWindowsMt5Modal} />
    </section>
  );
}
